import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component that:
 * - Scrolls to top on new navigations
 * - Preserves scroll position when using browser back/forward buttons
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();
  const previousPathname = useRef<string>(pathname);
  const isPopStateNavigation = useRef(false);

  useEffect(() => {
    // Enable browser's native scroll restoration for back/forward navigation
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }

    // Detect popstate (back/forward button) - this fires when user uses browser navigation
    const handlePopState = () => {
      // Set flag that persists until next pathname change
      isPopStateNavigation.current = true;
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    // Only act if pathname actually changed
    if (previousPathname.current === pathname) {
      return;
    }

    // If this is a back/forward navigation (popstate was detected), 
    // let the browser handle scroll restoration naturally
    if (isPopStateNavigation.current) {
      isPopStateNavigation.current = false;
      previousPathname.current = pathname;
      return;
    }

    // For new navigations (link clicks, programmatic navigation), scroll to top
    window.scrollTo(0, 0);
    
    previousPathname.current = pathname;
  }, [pathname]);

  return null;
}
